import React, { useRef } from 'react'
import { components, createFilter } from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'

import { fetchIngredients, fetchIngredient } from '@/api'
import { Ingredient, SearchIngredient, TempRecipeIngredient } from '@/api/types'
import { Button } from '@/components/Button'
import { Flex } from '@/components/Flex'
import Fuse from 'fuse.js'
import { withAsyncPaginate } from 'react-select-async-paginate'
import { Select } from '@/components/Select'

type Props = {
  currentSelection: TempRecipeIngredient[]
  addRecIngredient: (recIng: Ingredient) => void
}

const SelectPaginate = withAsyncPaginate(Select as any)

export default function IngredientsSearch({ currentSelection, addRecIngredient }: Props) {
  const dataPromiseRef = useRef<SearchIngredient[] | null>(null)
  const fuseRef = useRef<Fuse<SearchIngredient> | null>(null)

  async function loadOptions(search, _loadedOptions, { page }) {
    if (!dataPromiseRef.current) {
      dataPromiseRef.current = await fetchIngredients()
    }
    let data = dataPromiseRef.current

    if (search) {
      if (!fuseRef.current) {
        fuseRef.current = new Fuse(data, {
          keys: ['name', 'englishName'],
          threshold: 0.3,
        })
      }

      data = fuseRef.current.search(search).map((r) => r.item)
    }

    const offset = (page - 1) * 100

    return {
      options: data?.slice(offset, offset + 100),
      hasMore: data?.length > offset + 100,
      additional: {
        page: page + 1,
      },
    }
  }

  async function loadIngredient(recIng: SearchIngredient) {
    const ing = await fetchIngredient(recIng.idIngredient + '')

    addRecIngredient(ing)
  }

  return (
    <SelectPaginate
      loadOptions={loadOptions}
      filterOption={createFilter({ ignoreAccents: false })}
      additional={{
        page: 1,
      }}
      value={null}
      formatOptionLabel={(item) => (
        <Flex direction="column" gap={2}>
          <div>{item.name}</div>
          <div style={{ fontSize: 'small', color: 'gray' }}>{item.englishName}</div>
        </Flex>
      )}
      getOptionLabel={(item) => item.name + ' ' + item.englishName}
      getOptionValue={(item) => item.idIngredient.toString()}
      isOptionDisabled={(item) =>
        currentSelection.some((sel) => item.idIngredient === sel.ingredient.idIngredient)
      }
      onChange={loadIngredient}
      components={{
        DropdownIndicator,
        Placeholder,
        IndicatorSeparator: null,
      }}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          marginTop: 8,
          padding: 4,
          borderColor: 'var(--border-color)',
          backgroundColor: 'var(--field-background)',
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
        }),
      }}
    />
  )
}

function DropdownIndicator() {
  const { t } = useTranslation()
  return (
    <Button variant="primary" style={{ pointerEvents: 'none' }}>
      {t('recipe.addIngredient')}
    </Button>
  )
}

function Placeholder(props) {
  return (
    <components.Placeholder {...props}>
      <FontAwesomeIcon icon={faSearch} />
    </components.Placeholder>
  )
}
